import { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useData } from "../../../DataContext.js";

const SHAPES = ["All", "Tuck", "Pike", "Straight", "Straddle"];
const SHAPE_TO_FIG = { Tuck: "o", Pike: "<", Straight: "/", Straddle: "V" };
const DIRS   = ["All", "forwards", "backwards"];
const SOMS   = ["All", "0", "1", "2", "3+"];

const skillSearch = (s, q) => {
  const lower = q.toLowerCase();
  return s.name.toLowerCase().includes(lower) || (s.fig + s.shape).toLowerCase().includes(lower);
};

const ChipSelect = ({ label, options, value, onChange, dark }) => {
  const t = dark
    ? { label: "#556", active: "#93c5fd", activeBg: "#1e3a5f", activeBorder: "#3b82f6", idle: "#445", idleBg: "#0d1520", idleBorder: "#1a2535" }
    : { label: "#888", active: "#1d4ed8", activeBg: "#dbeafe", activeBorder: "#3b82f6", idle: "#666", idleBg: "#f1f5f9", idleBorder: "#cbd5e1" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: t.label, textTransform: "uppercase" }}>{label}</span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {options.map(opt => (
          <button key={opt} onClick={() => onChange(opt)} style={{
            padding: "3px 10px", borderRadius: 99, fontSize: 11, cursor: "pointer",
            fontWeight: value === opt ? 700 : 400,
            background: value === opt ? t.activeBg : t.idleBg,
            color: value === opt ? t.active : t.idle,
            border: `1px solid ${value === opt ? t.activeBorder : t.idleBorder}`,
            transition: "all 0.12s",
          }}>{opt}</button>
        ))}
      </div>
    </div>
  );
};

export default function TrampolineSkillMatrix({ skills, user }) {
  const [dark /*, setDark*/]                = useState(false);
  const [search1, setSearch1]               = useState("");
  const [search2, setSearch2]               = useState("");
  const [dir1, setDir1]                     = useState("All");
  const [dir2, setDir2]                     = useState("All");
  const [shape1, setShape1]                 = useState("All");
  const [shape2, setShape2]                 = useState("All");
  const [som1, setSom1]                     = useState("All");
  const [som2, setSom2]                     = useState("All");
  const [minDiff, setMinDiff]               = useState("");
  const [maxDiff, setMaxDiff]               = useState("");
  const [achievedFilter, setAchievedFilter] = useState("all");
  const [filtersOpen, setFiltersOpen]       = useState(false);
  const [comboList, setComboList]           = useState(null);
  const [isTokenReady, setIsTokenReady]     = useState(false);
  const { triggerAchievements } = useData();


  useEffect(() => {
    const fetchJwtToken = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken;
        axios.defaults.headers['Authorization'] = `Bearer ${token}`;
        setIsTokenReady(true); 
      } catch (error) {
        console.error('Error fetching JWT token:', error);
        setIsTokenReady(true); 
      }
    };

    fetchJwtToken();
  }, []);

  useEffect(() => {
    if (isTokenReady) {
        axios.get(process.env.REACT_APP_API_HOST + '/api/combos/' + user.userId)
        .then(response => {
            const data = response.data;
            if (data !== null) setComboList(data);
        })
        .catch(error => console.error('Error fetching combo list:', error));
    }
    }, [user.userId, isTokenReady]);

  const t = dark ? {
    bg: "#080e18", surface: "#0a1218", surfaceDone: "#0d1f10",
    border: "#0f1a24", borderDone: "#1a4a20",
    heading: "#e0f0ff", subtext: "#334",
    textDim: "#4a6a7a", textMuted: "#2a3a4a",
    accent: "#4ade80", accentBlue: "#60a5fa",
    inputBg: "#0a1520", inputBorder: "#1a2a3a", inputText: "#c8ddf0",
    checkBorder: "#1a3a2a", checkBg: "#4ade80", checkMark: "#080e18",
    arrow: "#1e3a5f",
    skillName: "#7a9aaa", skillNameDone: "#86efac",
    skillFig: "#2a4a5a", skillFigDone: "#4a8a60",
    dd: "#2a4a5a", ddDone: "#4ade80",
    som: "#1e3a5f",
    filterBg: "#0a1220",
    toggleBg: "#0d1520", toggleBorder: "#1a2a3a", toggleText: "#4a7a9b",
    countLabel: "#334",
    headerBorder: "#0f1e2e",
  } : {
    bg: "#f0f4f8", surface: "#ffffff", surfaceDone: "#f0fdf4",
    border: "#e2e8f0", borderDone: "#86efac",
    heading: "#0f172a", subtext: "#64748b",
    textDim: "#64748b", textMuted: "#94a3b8",
    accent: "#16a34a", accentBlue: "#2563eb",
    inputBg: "#ffffff", inputBorder: "#cbd5e1", inputText: "#1e293b",
    checkBorder: "#86efac", checkBg: "#16a34a", checkMark: "#ffffff",
    arrow: "#94a3b8",
    skillName: "#475569", skillNameDone: "#15803d",
    skillFig: "#94a3b8", skillFigDone: "#4ade80",
    dd: "#94a3b8", ddDone: "#16a34a",
    som: "#bfdbfe",
    filterBg: "#f8fafc",
    toggleBg: "#ffffff", toggleBorder: "#cbd5e1", toggleText: "#475569",
    countLabel: "#94a3b8",
    headerBorder: "#e2e8f0",
  };

  const matchSom = (skill, val) => {
    if (val === "All") return true;
    if (val === "3+") return skill.somersaults >= 3;
    return skill.somersaults === parseInt(val);
  };

  const ticked = useMemo(() => {
    return new Set((comboList || []).map(c => `${c.skill_1_id}-${c.skill_2_id}`));
    }, [comboList]);

  const pairs = useMemo(() => {
    const results = [];
    for (const s1 of skills) {
      for (const s2 of skills) {
        if (s1.to !== s2.from) continue;
        if (search1 && !skillSearch(s1, search1)) continue;
        if (search2 && !skillSearch(s2, search2)) continue;
        if (dir1   !== "All" && s1.direction !== dir1)   continue;
        if (dir2   !== "All" && s2.direction !== dir2)   continue;
        if (shape1 !== "All" && s1.shape     !== SHAPE_TO_FIG[shape1]) continue;
        if (shape2 !== "All" && s2.shape     !== SHAPE_TO_FIG[shape2]) continue;
        if (!matchSom(s1, som1)) continue;
        if (!matchSom(s2, som2)) continue;
        const combo = s1.difficulty + s2.difficulty;
        if (minDiff !== "" && combo < parseFloat(minDiff)) continue;
        if (maxDiff !== "" && combo > parseFloat(maxDiff)) continue;
        results.push({ s1, s2, key: `${s1.id}-${s2.id}`, combo, totalSom: s1.somersaults + s2.somersaults });
      }
    }
    return results;
  }, [search1, search2, dir1, dir2, shape1, shape2, som1, som2, minDiff, maxDiff, skills]);

  const visiblePairs = useMemo(() => {
    if (achievedFilter === "achieved")     return pairs.filter(p => ticked.has(p.key));
    if (achievedFilter === "not-achieved") return pairs.filter(p => !ticked.has(p.key));
    return pairs;
  }, [pairs, achievedFilter, ticked]);

const toggleTick = (key, s1id, s2id) => {
    if (ticked.has(key)) {
        axios.delete(process.env.REACT_APP_API_HOST + '/api/combos', {
        data: { user_id: user.userId, skill_1_id: s1id, skill_2_id: s2id }
        })
        .then(() => setComboList(prev => prev.filter(c => !(c.skill_1_id === s1id && c.skill_2_id === s2id))))
        .catch(error => console.error('Error removing combo:', error));
    } else {
        axios.post(process.env.REACT_APP_API_HOST + '/api/combos', {
        user_id: user.userId, skill_1_id: s1id, skill_2_id: s2id
        })
        .then((response) => {
            setComboList(prev => [...(prev || []), { user_id: user.userId, skill_1_id: s1id, skill_2_id: s2id }]);
            const { new_achievements } = response.data;
            triggerAchievements(new_achievements);
        })
        .catch(error => console.error('Error adding combo:', error));
    }
};

  const totalPossible = useMemo(() => {
    let n = 0;
    for (const s1 of skills) for (const s2 of skills) if (s1.to === s2.from) n++;
    return n;
  }, [skills]);

  const inputStyle = {
    background: t.inputBg, border: `1px solid ${t.inputBorder}`, borderRadius: 6,
    padding: "7px 10px", color: t.inputText, fontSize: 12, outline: "none",
    width: "100%", boxSizing: "border-box", fontFamily: "inherit",
  };

  return (
    <div className="entries-in-tabs" style={{ maxHeight: 'calc(100dvh - 135px)', fontFamily: "'DM Mono', 'Courier New', monospace", background: t.bg, padding: 20, color: t.textDim, borderRadius: 10 }}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 20, borderBottom: `1px solid ${t.headerBorder}`, paddingBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              {/* <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: t.heading, letterSpacing: -0.5 }}>Skills Matrix</h2> */}
              <p style={{ margin: "4px 0 0", fontSize: 12, color: t.subtext }}>Track which combos you can link</p>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: t.accent }}>{ticked.size}</div>
                <div style={{ fontSize: 10, color: t.countLabel, letterSpacing: 1 }}>ACHIEVED</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: t.accentBlue }}>{totalPossible}</div>
                <div style={{ fontSize: 10, color: t.countLabel, letterSpacing: 1 }}>POSSIBLE</div>
              </div>
              {/* <button onClick={() => setDark(d => !d)} style={{
                background: t.toggleBg, border: `1px solid ${t.toggleBorder}`,
                borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1,
              }}>{dark ? "☀️" : "🌙"}</button> */}
            </div>
          </div>
        </div>

        {/* Search row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <input placeholder="Search skill 1…" value={search1} onChange={e => setSearch1(e.target.value)} style={inputStyle} />
          <span style={{ color: t.arrow, fontSize: 18, textAlign: "center" }}>→</span>
          <input placeholder="Search skill 2…" value={search2} onChange={e => setSearch2(e.target.value)} style={inputStyle} />
        </div>

        {/* Achieved filter + filter toggle */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", gap: 4, background: t.filterBg, borderRadius: 8, padding: 3, border: `1px solid ${t.border}` }}>
            {[["all", "All"], ["achieved", "✓ Achieved"], ["not-achieved", "✗ Not yet"]].map(([val, label]) => (
              <button key={val} onClick={() => setAchievedFilter(val)} style={{
                padding: "4px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", border: "none",
                fontWeight: achievedFilter === val ? 700 : 400,
                background: achievedFilter === val ? (dark ? "#1a3a2a" : "#dcfce7") : "transparent",
                color: achievedFilter === val ? t.accent : t.textDim,
                transition: "all 0.12s",
              }}>{label}</button>
            ))}
          </div>
          <button onClick={() => setFiltersOpen(o => !o)} style={{
            background: t.toggleBg, border: `1px solid ${t.toggleBorder}`, borderRadius: 6,
            padding: "5px 12px", color: t.toggleText, fontSize: 11, cursor: "pointer",
          }}>{filtersOpen ? "▲ Hide filters" : "▼ More filters"}</button>
        </div>

        {/* Filter panel */}
        {filtersOpen && (
          <div style={{ background: t.filterBg, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: t.textMuted, letterSpacing: 2, textTransform: "uppercase" }}>Skill 1</div>
                <ChipSelect label="Direction"   options={DIRS}   value={dir1}   onChange={setDir1}   dark={dark} />
                <ChipSelect label="Shape"       options={SHAPES} value={shape1} onChange={setShape1} dark={dark} />
                <ChipSelect label="Somersaults" options={SOMS}   value={som1}   onChange={setSom1}   dark={dark} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: t.textMuted, letterSpacing: 2, textTransform: "uppercase" }}>Skill 2</div>
                <ChipSelect label="Direction"   options={DIRS}   value={dir2}   onChange={setDir2}   dark={dark} />
                <ChipSelect label="Shape"       options={SHAPES} value={shape2} onChange={setShape2} dark={dark} />
                <ChipSelect label="Somersaults" options={SOMS}   value={som2}   onChange={setSom2}   dark={dark} />
              </div>
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: t.textMuted, textTransform: "uppercase", marginBottom: 6 }}>Combo Difficulty Range</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input placeholder="Min" value={minDiff} onChange={e => setMinDiff(e.target.value)} style={{ ...inputStyle, width: 80 }} />
                <span style={{ color: t.textMuted }}>–</span>
                <input placeholder="Max" value={maxDiff} onChange={e => setMaxDiff(e.target.value)} style={{ ...inputStyle, width: 80 }} />
              </div>
            </div>
            <button onClick={() => { setDir1("All"); setDir2("All"); setShape1("All"); setShape2("All"); setSom1("All"); setSom2("All"); setMinDiff(""); setMaxDiff(""); setSearch1(""); setSearch2(""); }}
              style={{ marginTop: 12, background: "none", border: "none", color: t.textDim, fontSize: 11, cursor: "pointer", padding: 0 }}>
              ✕ Clear all filters
            </button>
          </div>
        )}

        {/* Results count */}
        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 8 }}>
          {visiblePairs.length.toLocaleString()} combo{visiblePairs.length !== 1 ? "s" : ""} shown
        </div>

        {/* Column headers */}
        <div style={{
          display: "grid", gridTemplateColumns: "24px 1fr 24px 1fr 44px",
          gap: 8, padding: "5px 10px", marginBottom: 4,
          fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: t.textMuted, textTransform: "uppercase",
        }}>
          <span /><span>Skill 1</span><span /><span>Skill 2</span>
          <span style={{ textAlign: "right" }}>DD</span>
          {/* <span style={{ textAlign: "right" }}>SOM</span> */}
        </div>

        {/* Pair list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {visiblePairs.slice(0, 200).map(({ s1, s2, key, combo, totalSom }) => {
            const done = ticked.has(key);
            return (
              <div key={key} onClick={() => toggleTick(key, s1.id, s2.id)} style={{
                display: "grid", gridTemplateColumns: "24px 1fr 24px 1fr 44px",
                gap: 8, alignItems: "start",
                padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                background: done ? t.surfaceDone : t.surface,
                border: `1px solid ${done ? t.borderDone : t.border}`,
                transition: "background 0.1s, border-color 0.1s",
              }}>
                {/* Checkbox — nudged down to align with first line of text */}
                <div style={{ paddingTop: 2, display: "flex", alignItems: "flex-start" }}>
                  <div style={{
                    width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                    border: `2px solid ${done ? t.checkBg : t.checkBorder}`,
                    background: done ? t.checkBg : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {done && <span style={{ color: t.checkMark, fontSize: 10, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                  </div>
                </div>

                {/* Skill 1 */}
                <div>
                  <div style={{ fontSize: 12, color: done ? t.skillNameDone : t.skillName, fontWeight: done ? 600 : 400, lineHeight: 1.4 }}>
                    {s1.name}{" "}
                    <span style={{ fontSize: 11, color: done ? t.skillFigDone : t.skillFig, fontWeight: 400 }}>({s1.fig}{s1.shape})</span>
                  </div>
                </div>

                {/* Arrow — nudged to align with first text line */}
                <div style={{ paddingTop: 1, color: t.arrow, fontSize: 14, textAlign: "center" }}>→</div>

                {/* Skill 2 */}
                <div>
                  <div style={{ fontSize: 12, color: done ? t.skillNameDone : t.skillName, fontWeight: done ? 600 : 400, lineHeight: 1.4 }}>
                    {s2.name}{" "}
                    <span style={{ fontSize: 11, color: done ? t.skillFigDone : t.skillFig, fontWeight: 400 }}>({s2.fig}{s2.shape})</span>
                  </div>
                </div>

                {/* DD */}
                <div style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: done ? t.ddDone : t.dd, paddingTop: 1 }}>
                  {combo.toFixed(1)}
                </div>

                {/* Somersaults */}
                {/* <div style={{ textAlign: "right", fontSize: 11, color: done ? t.accent : t.som, paddingTop: 1 }}>
                  {totalSom}
                </div> */}
              </div>
            );
          })}

          {visiblePairs.length > 200 && (
            <div style={{ textAlign: "center", padding: 12, fontSize: 11, color: t.textMuted }}>
              Showing first 200 of {visiblePairs.length.toLocaleString()} — refine filters to narrow down
            </div>
          )}
          {visiblePairs.length === 0 && (
            <div style={{ textAlign: "center", padding: 32, fontSize: 13, color: t.textMuted }}>
              No combos match your filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
}